import { useCallback, useEffect, useState } from 'react';
import { getWorkflowAccessToken, isAuthConfigured, loginRequest, msal, photoLibraryEnabled, workflowEnabled } from './auth';
import { fetchLibraryVariant, getPhotoLibraryAccess, listPhotoLibrary } from './workflow-api';
import './photo-library.css';

function Preview({ photo, getAccessToken }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setUrl(''); setError('');
    fetchLibraryVariant(photo, 'applied', getAccessToken, true).then(blob => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob); setUrl(objectUrl);
    }).catch(error => { if (active) setError(error.message); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photo.employeeId, photo.versionId, getAccessToken]);
  return url ? <img src={url} alt={`Latest applied photo for ${photo.name}`} /> : <p>{error || 'Loading preview…'}</p>;
}
export default function PhotoLibraryApp() {
  const [account, setAccount] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [query, setQuery] = useState('');
  const [company, setCompany] = useState('');
  const [filters, setFilters] = useState({ query: '', company: '' });
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const getAccessToken = useCallback(() => getWorkflowAccessToken(account), [account]);
  useEffect(() => {
    if (!photoLibraryEnabled || !workflowEnabled || !isAuthConfigured || !msal) { setStatus('disabled'); return; }
    let active = true;
    (async () => {
      try {
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const user = redirect?.account || msal.getAllAccounts()[0];
        if (!active) return;
        if (!user) { setStatus('anonymous'); return; }
        msal.setActiveAccount(user); setAccount(user);
        const access = await getPhotoLibraryAccess(() => getWorkflowAccessToken(user));
        if (active) { setIdentity(access.identity); setStatus('ready'); }
      } catch (error) { if (active) { setError(error.message); setStatus('forbidden'); } }
    })();
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (status !== 'ready') return;
    let active = true;
    setBusy(true); setError(''); setPhotos([]); setCursor(null); setSelected(null);
    listPhotoLibrary(getAccessToken, filters).then(result => {
      if (active) { setPhotos(result.photos); setCursor(result.nextCursor); }
    }).catch(error => { if (active) setError(error.message); }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [status, filters, getAccessToken]);
  async function loadMore() {
    setBusy(true); setError('');
    try {
      const result = await listPhotoLibrary(getAccessToken, { ...filters, cursor });
      setPhotos(current => [...current, ...result.photos.filter(photo => !current.some(old => old.employeeId === photo.employeeId))]);
      setCursor(result.nextCursor);
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  }
  async function download(photo, variant) {
    setBusy(true); setError('');
    try {
      const blob = await fetchLibraryVariant(photo, variant, getAccessToken);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `${photo.employeeId}-${variant}.${blob.type === 'image/png' ? 'png' : 'jpg'}`;
      link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  }
  async function signIn() {
    try { await msal.loginRedirect(loginRequest); } catch (error) { setError(error.message); }
  }
  if (status !== 'ready') return <main className="auth-shell"><section className="auth-card">
    <p className="eyebrow">Profile Picture Studio</p><h1>Photo Library</h1>
    <p>{status === 'loading' ? 'Checking access…' : status === 'disabled' ? 'Photo Library is not enabled in this environment.' : status === 'anonymous' ? 'Sign in to browse employee photos.' : 'Photo Library access is required.'}</p>
    {status !== 'loading' && status !== 'disabled' && <button onClick={signIn}>Sign in with Microsoft</button>}
    {error && <p role="alert">{error}</p>}<a href="/">Back to Studio</a>
  </section></main>;
  return <main className="photo-library-app">
    <header><div><p className="eyebrow">Profile Picture Studio</p><h1>Employee Photo Library</h1><p>Latest successfully applied photos · All companies</p><small>{identity.name || identity.email}</small></div>
      <nav><a href="/">Studio</a><button onClick={() => msal.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin }).catch(error => setError(error.message))}>Sign out</button></nav></header>
    <form onSubmit={event => { event.preventDefault(); setFilters({ query: query.trim(), company: company.trim() }); }}>
      <label>Name or email prefix<input value={query} maxLength={80} onChange={event => setQuery(event.target.value)} placeholder="Start typing an employee name or email" /></label>
      <label>Company<input value={company} maxLength={200} onChange={event => setCompany(event.target.value)} placeholder="Exact company name (optional)" /></label>
      <button disabled={busy}>Search / Refresh</button>
    </form>
    <p className="photo-library-note">Original = untouched upload. Applied = exact JPEG sent to Microsoft 365. Access is audited. Originals are unavailable for older requests that did not retain them.</p>
    {error && <p className="workflow-error" role="alert">{error}</p>}
    <section className="photo-library-grid" aria-busy={busy}>
      {photos.map(photo => <article key={photo.employeeId}>
        <button className="photo-library-preview-button" onClick={() => setSelected(selected?.employeeId === photo.employeeId ? null : photo)} disabled={busy}>{selected?.employeeId === photo.employeeId ? 'Hide preview' : 'Preview applied photo'}</button>
        {selected?.employeeId === photo.employeeId && <Preview photo={photo} getAccessToken={getAccessToken} />}
        <h2>{photo.name}</h2><p>{photo.email}</p><p>{photo.company}</p>
        <p>{[photo.department, photo.jobTitle].filter(Boolean).join(' · ') || 'Department / job title unavailable'}</p>
        <small>Applied {new Date(photo.completedAt).toLocaleString()}</small>
        <footer><button disabled={busy || !photo.originalAvailable} onClick={() => download(photo, 'original')}>{photo.originalAvailable ? 'Download Original' : 'Original unavailable'}</button>
          <button disabled={busy || !photo.appliedAvailable} onClick={() => download(photo, 'applied')}>Download Applied</button></footer>
      </article>)}
    </section>
    {!busy && !photos.length && <p>{cursor ? 'No matches on this page. Continue to the next page.' : 'No retained photos match this search.'}</p>}
    {busy && <p role="status">Loading…</p>}
    {cursor && <button disabled={busy} onClick={loadMore}>Load more</button>}
  </main>;
}
